/**
 * Normalizes GET /api/reports/:sessionId response for UI state.
 * @param {{ data?: object|null, generating?: boolean }} rr
 * @returns {{ report: object|null, generating: boolean, done: boolean }}
 */
export function parseReportFetchResult(rr) {
    if (rr?.data) {
        return { report: rr.data, generating: false, done: true };
    }
    if (rr?.generating) {
        return { report: null, generating: true, done: false };
    }
    return { report: null, generating: false, done: true };
}

/** @returns {boolean} true if polling should stop (report ready or terminal state) */
export function shouldStopReportPolling({ done, startedAt, maxMs = 5 * 60 * 1000 }) {
    if (done) return true;
    return Date.now() - startedAt > maxMs;
}

/**
 * Merge monitoring / proctoring fields from InterviewReport and InterviewSession.
 * Report wins when present; session fills gaps (e.g. before report is generated).
 */
export function mergeMonitoringData(report, session) {
    const r = report || {};
    const s = session || {};
    const it = s.integrityTracking || {};

    const pickArr = (a, b) => (Array.isArray(a) && a.length ? a : Array.isArray(b) ? b : []);

    return {
        integrityScore: r.integrityScore ?? null,
        integrityBreakdown: r.integrityBreakdown ?? null,
        gazeOnScreenPct: r.gazeOnScreenPct ?? s.gazeOnScreenPct ?? null,
        tabSwitchCount: r.tabSwitchCount ?? s.tabSwitchCount ?? 0,
        voiceEvents: pickArr(r.voiceEvents, s.voiceEvents),
        noFaceTotalSecs: r.noFaceTotalSecs ?? it.noFaceTotalSecs ?? 0,
        cameraCoveredCount: r.cameraCoveredCount ?? it.cameraCoveredCount ?? 0,
        multipleFacesCount: r.multipleFacesCount ?? it.multipleFacesCount ?? 0,
        multipleFacesIncidents: r.multipleFacesIncidents ?? it.multipleFacesIncidents ?? null,
        lipSyncFlags: r.lipSyncFlags ?? it.lipSyncFlags ?? 0,
        dualAudioFlags: r.dualAudioFlags ?? it.dualAudioFlags ?? 0,
        lookingAwayPct: r.lookingAwayPct ?? it.lookingAwayPct ?? null,
        warningCount: r.warningCount ?? s.warningCount ?? 0,
        proctoringInfractions: pickArr(r.proctoringInfractions, s.proctoringInfractions),
        proctoringEventLog: pickArr(r.proctoringEventLog, s.proctoringEventLog),
        joinImages: pickArr(r.joinImages, s.joinImages),
        questionImages: pickArr(r.questionImages, s.questionImages),
        fromReport: !!report,
        fromSession: !!session,
    };
}

/** @param {ReturnType<typeof mergeMonitoringData>} m */
export function hasMonitoringData(m) {
    if (!m) return false;
    return (
        m.gazeOnScreenPct != null
        || m.tabSwitchCount > 0
        || m.voiceEvents.length > 0
        || m.noFaceTotalSecs > 0
        || m.cameraCoveredCount > 0
        || m.multipleFacesCount > 0
        || (m.multipleFacesIncidents != null && m.multipleFacesIncidents > 0)
        || m.lipSyncFlags > 0
        || m.dualAudioFlags > 0
        || m.lookingAwayPct != null
        || m.joinImages.length > 0
        || m.questionImages.length > 0
        || m.proctoringInfractions.length > 0
        || m.proctoringEventLog.length > 0
        || m.warningCount > 0
    );
}

/** Mirrors backend calculateIntegrityScore for display when report not yet generated. */
export function calculateIntegrityScore(tracking) {
    let score = 100;
    const breakdown = {
        tabSwitchDeduction: 0,
        multipleFacesDeduction: 0,
        lipSyncDeduction: 0,
        sidewaysGazeDeduction: 0,
        dualAudioDeduction: 0,
    };

    const tabDed = Math.min(40, (tracking.tabSwitchCount || 0) * 20);
    breakdown.tabSwitchDeduction = tabDed;
    score -= tabDed;

    const rawFaceFrames = tracking.multiple_faces_count ?? tracking.multipleFacesCount ?? 0;
    const faceIncidents = tracking.multiple_faces_incidents ?? tracking.multipleFacesIncidents ?? Math.ceil(rawFaceFrames / 2);
    const facesDed = Math.min(25, faceIncidents * 5);
    breakdown.multipleFacesDeduction = facesDed;
    score -= facesDed;

    const rawLip = tracking.lip_sync_flags ?? tracking.lipSyncFlags ?? 0;
    const lipDed = rawLip < 3 ? 0 : Math.min(20, (rawLip - 2) * 5);
    breakdown.lipSyncDeduction = lipDed;
    score -= lipDed;

    const gazeOnScreen = tracking.gaze_on_screen_pct ?? tracking.gazeOnScreenPct ?? 100;
    const gazeAwayPct = Math.max(0, 100 - gazeOnScreen);
    const gazeDed = Math.min(20, gazeAwayPct * 0.3);
    breakdown.sidewaysGazeDeduction = Math.round(gazeDed * 10) / 10;
    score -= gazeDed;

    const dualDed = Math.min(20, (tracking.dual_audio_flags ?? tracking.dualAudioFlags ?? 0) * 10);
    breakdown.dualAudioDeduction = dualDed;
    score -= dualDed;

    return { score: Math.max(0, Math.round(score)), breakdown };
}

/** @param {ReturnType<typeof mergeMonitoringData>} m */
export function resolveIntegrityDisplay(m) {
    if (m.integrityScore != null) {
        return { score: m.integrityScore, breakdown: m.integrityBreakdown || null, computed: false };
    }
    if (!hasMonitoringData(m)) return { score: null, breakdown: null, computed: false };
    const { score, breakdown } = calculateIntegrityScore({
        tabSwitchCount: m.tabSwitchCount,
        multipleFacesCount: m.multipleFacesCount,
        multipleFacesIncidents: m.multipleFacesIncidents,
        lipSyncFlags: m.lipSyncFlags,
        gazeOnScreenPct: m.gazeOnScreenPct,
        dualAudioFlags: m.dualAudioFlags,
    });
    return { score, breakdown, computed: true };
}

const TRACKING_EVENT_KEYS = [
    ['no_face_count', 'No face frames'],
    ['multiple_faces_count', 'Multiple faces'],
    ['multiple_faces_incidents', 'Face incidents'],
    ['lip_sync_flags', 'Lip-sync flags'],
    ['dual_audio_flags', 'Dual audio'],
    ['camera_covered_count', 'Camera covered'],
    ['gaze_on_screen_pct', 'Gaze on screen %'],
    ['looking_away_pct', 'Looking away %'],
    ['tab_switch_count', 'Tab switches'],
];

/** Human-readable one-line summary for a proctoring log / infraction entry. */
export function formatProctoringEntry(entry) {
    if (entry == null) return '—';
    if (typeof entry === 'string') return entry;
    if (entry.type) return String(entry.type);
    if (entry.reason) return String(entry.reason);

    const parts = [];
    for (const [key, label] of TRACKING_EVENT_KEYS) {
        if (entry[key] != null && entry[key] !== 0) parts.push(`${label}: ${entry[key]}`);
    }
    if (parts.length) return parts.join(' · ');
    try {
        const s = JSON.stringify(entry);
        return s.length > 120 ? `${s.slice(0, 117)}…` : s;
    } catch {
        return 'Event';
    }
}

export function formatEventTime(entry) {
    const ts = entry?.time ?? entry?.timestamp ?? entry?.at;
    if (!ts) return '—';
    try {
        return new Date(ts).toLocaleString([], { dateStyle: 'short', timeStyle: 'medium' });
    } catch {
        return '—';
    }
}
